import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import PageHero from "../components/child/PageHero";
import ToggleBar from "../components/child/ToggleBar";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import "./ScanInstance.css";

const ImageNotFound = "/assets/images/image-not-found.jpeg";

const STEPS = [
    {
        text: (
            <>
                Open WhatsApp on your phone
            </>
        ),
    },
    {
        text: (
            <>
                Tap Menu <Icon icon="tabler:dots-vertical" width={16} /> (Android) or Settings{" "}
                <Icon icon="carbon:settings" width={16} /> (iPhone)
            </>
        ),
    },
    {
        text: <>Tap Linked devices → Link a device</>,
    },
    {
        text: <>Point your phone at the QR code on the right</>,
    },
];

const ScanInstance = () => {
    const navigate = useNavigate();
    const intervalRef = useRef(null);
    const [qrcode, setQrcode] = useState(null);
    const [name, setName] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [spinner, setSpinner] = useState(false);
    const [isNewInstance, setIsNewInstance] = useState(false);
    const [selectedInstance, setSelectedInstance] = useState("");
    const [delay, setDelay] = useState(0);
    const [instances, setInstances] = useState([]);
    const [setting, setSetting] = useState({ isAddQueueLog: false });

    const hideAlert = (sec = 3) => {
        setTimeout(() => setAlertMessage(""), sec * 1000);
    };

    const checkAvailability = async (id) => {
        try {
            const data = await apiCall({
                url: `sessions/status/${id}`,
                method: "GET",
            });
            if (data.success) {
                if (intervalRef.current) clearInterval(intervalRef.current);
                setAlertMessage({
                    message: "Connected! Redirecting to sessions…",
                    class: "success",
                });
                setTimeout(() => navigate("/instances"), 1500);
            } else {
                setAlertMessage({
                    message: "Waiting for QR scan — keep this screen open.",
                    class: "danger",
                });
            }
        } catch (error) {
            console.error("Error checking availability:", error);
        }
    };

    const refreshLiveLimits = async () => {
        const [userRes, listRes] = await Promise.all([
            apiCall({ url: "user/get-user-by-token", method: "GET" }),
            apiCall({
                url: "sessions/get-users-instances?is_list_instance=true",
                method: "GET",
            }),
        ]);

        const user = userRes?.success ? userRes.data : null;
        if (user) {
            try {
                localStorage.setItem("user", JSON.stringify(user));
            } catch {
                /* ignore */
            }
        }

        let planInstance = 0;
        try {
            const plan = typeof user?.plan === "string" ? JSON.parse(user.plan) : user?.plan;
            planInstance = Number(plan?.instance) || 0;
        } catch {
            planInstance = 0;
        }
        const instanceLimit = Math.max(
            0,
            Number(user?.allow_multi_instance) || planInstance || 0
        );
        localStorage.setItem("instanceLimit", String(instanceLimit));

        const list = Array.isArray(listRes?.data) ? listRes.data : [];
        setInstances(list);
        localStorage.setItem("instanceTotal", String(list.length));
        if (!list.length) setIsNewInstance(true);

        return { instanceLimit, instanceTotal: list.length, user };
    };

    const generateQrcode = async () => {
        try {
            // Always check live quota — login-time localStorage can be stale after plan activate.
            const { instanceLimit, instanceTotal } = await refreshLiveLimits();
            if (isNewInstance && instanceTotal >= instanceLimit) {
                setAlertMessage({
                    message: `You have reached the limit of instances (${instanceTotal}/${instanceLimit}). Upgrade your plan to add more.`,
                    class: "danger",
                });
                hideAlert(10);
                setSpinner(false);
                return;
            }

            let id;
            if (selectedInstance === "" || isNewInstance) {
                const uid = localStorage.getItem("uid");
                id = btoa(JSON.stringify({ id: uid, client_id: name }));
            } else {
                id = selectedInstance;
            }

            const data = await apiCall({
                url: `sessions/add`,
                method: "Post",
                body: { id, isLegacy: true, name, isNewInstance, delay },
            });

            if (data.success) {
                setQrcode(data.data.qr);
                setAlertMessage({
                    message: "Scan the QR code and wait — session is getting ready.",
                    class: "success",
                });
                if (intervalRef.current) clearInterval(intervalRef.current);
                intervalRef.current = setInterval(() => checkAvailability(id), 10000);
            } else {
                setAlertMessage({
                    message: data.msg ?? data.message,
                    class: "danger",
                });
            }
            setSpinner(false);
        } catch (error) {
            console.error("Error generating QR:", error);
            setAlertMessage({
                message: error.message || "Could not generate QR",
                class: "danger",
            });
            hideAlert(5);
            setSpinner(false);
        }
    };

    const handleSumbit = async (e) => {
        e.preventDefault();
        setSpinner(true);
        generateQrcode();
    };

    const fetchInstances = async () => {
        try {
            await refreshLiveLimits();
        } catch (error) {
            console.error("Error fetching instances:", error);
        }
    };

    const handleSelectInstance = (event) => {
        const instance = instances.find((i) => i.client_id == event.target.value);
        setName(instance?.name || "");
        setSelectedInstance(event.target.value);
    };

    useEffect(() => {
        fetchInstances();
        document.title = "Scan Instance";
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setSetting({ isAddQueueLog: user.isAddQueueLog });
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    return (
        <MasterLayout>
            <div className="scan-page">
                <PageHero
                    title="Scan instance"
                    subtitle="Link a WhatsApp device by scanning the QR code. Keep your phone nearby."
                    badge={{ icon: "mdi:qrcode-scan", label: "Connect" }}
                    action={
                        <Link to="/instances" className="btn btn-outline-success btn-sm scan-back-btn">
                            Back to sessions
                        </Link>
                    }
                />

                <div className="scan-grid">
                    <div className="scan-card">
                        <div className="scan-card-head">
                            <h6>How to link WhatsApp</h6>
                            <span className="scan-card-head-meta">4 quick steps</span>
                        </div>
                        <div className="scan-card-body">
                            <div className="scan-note">
                                <span className="scan-note-icon">
                                    <Icon icon="mdi:information-outline" width={18} />
                                </span>
                                <span>
                                    Use the phone that owns the WhatsApp account you want to connect.
                                </span>
                            </div>
                            <ol className="scan-steps">
                                {STEPS.map((step, index) => (
                                    <li key={index} className="scan-step">
                                        <span className="scan-step-num">{index + 1}</span>
                                        <p className="scan-step-text">{step.text}</p>
                                    </li>
                                ))}
                            </ol>
                            <a
                                href="https://faq.whatsapp.com/1317564962315842/?cms_platform=web&lang=en"
                                target="_blank"
                                rel="noreferrer"
                                className="scan-help-link"
                            >
                                Need help getting started?
                                <Icon icon="mdi:open-in-new" width={16} />
                            </a>
                        </div>
                    </div>

                    <div className="scan-card">
                        <div className="scan-card-head">
                            <h6>QR & session</h6>
                            <span className="scan-card-head-meta">
                                {qrcode ? "Ready to scan" : "Generate QR"}
                            </span>
                        </div>
                        <div className="scan-card-body">
                            {alertMessage?.message && (
                                <div className={`scan-alert ${alertMessage.class}`}>
                                    {alertMessage.message}
                                </div>
                            )}

                            <div className="scan-qr-frame">
                                <div className="scan-qr-corners" aria-hidden="true">
                                    <span />
                                    <span />
                                </div>
                                {qrcode ? (
                                    <img
                                        src={qrcode}
                                        alt="WhatsApp QR code"
                                        className="scan-qr-img"
                                    />
                                ) : (
                                    <div className="scan-qr-placeholder">
                                        <img
                                            src={ImageNotFound}
                                            alt=""
                                            className="scan-qr-img"
                                            style={{ opacity: 0.55, maxWidth: 160 }}
                                        />
                                        <strong>QR will appear here</strong>
                                        <span>Choose an instance below, then generate a code.</span>
                                    </div>
                                )}
                            </div>

                            <form onSubmit={handleSumbit}>
                                {instances.length > 0 && (
                                    <ToggleBar
                                        id="scan-new-instance"
                                        label="Create new instance?"
                                        description={
                                            isNewInstance
                                                ? "A fresh session name will be created"
                                                : "Reuse an existing session instead"
                                        }
                                        checked={isNewInstance}
                                        onChange={(checked) => setIsNewInstance(checked)}
                                    />
                                )}

                                {(isNewInstance || instances.length === 0) && (
                                    <div className="scan-field">
                                        <label className="scan-label" htmlFor="scan-name">
                                            Instance name
                                        </label>
                                        <input
                                            id="scan-name"
                                            type="text"
                                            className="scan-input"
                                            placeholder="e.g. ETQAN Sales"
                                            value={name}
                                            required
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                )}

                                {!isNewInstance && instances.length > 0 && (
                                    <div className="scan-field">
                                        <label className="scan-label" htmlFor="scan-select">
                                            Existing instance
                                        </label>
                                        <div className="scan-select-wrap">
                                            <span className="scan-select-leading">
                                                <Icon icon="mdi:server-network" width={18} />
                                            </span>
                                            <select
                                                id="scan-select"
                                                className="scan-select"
                                                required
                                                value={selectedInstance}
                                                onChange={handleSelectInstance}
                                            >
                                                <option value="">Choose instance</option>
                                                {instances.map((instance) => (
                                                    <option
                                                        key={instance.id}
                                                        value={instance.client_id}
                                                    >
                                                        {instance.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="scan-select-caret">
                                                <Icon icon="mdi:chevron-down" width={20} />
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {setting.isAddQueueLog === true && (
                                    <div className="scan-field">
                                        <label className="scan-label" htmlFor="scan-delay">
                                            Delay (seconds)
                                        </label>
                                        <input
                                            id="scan-delay"
                                            type="number"
                                            className="scan-input"
                                            placeholder="0"
                                            value={delay}
                                            required
                                            onChange={(e) => setDelay(e.target.value)}
                                        />
                                    </div>
                                )}

                                <div className="scan-actions">
                                    <button
                                        type="submit"
                                        className="scan-submit"
                                        disabled={spinner}
                                    >
                                        {spinner ? "Generating QR…" : "Generate QR"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </MasterLayout>
    );
};

export default ScanInstance;
